// src/pages/Reports/IssuanceReporting/DistributionRecall/DeliveryLinesTab.tsx
// RPT-006 (README.md mục 7) — chi tiết giao vé cho đại lý, 1 dòng = 1 dải số trong 1 phiếu giao.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Input, Label, Row, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';

import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import AgentPagination from 'pages/AgentManagement/AgentPagination';
import { getTicketDeliveryLineReportApi } from 'helpers/fakebackend_helper';
import { ISSUANCE_REPORTS_BASE } from 'helpers/url_helper';
import { buildAgentMap, getAgentCode, getAgentName, loadAgentsWithCache } from 'helpers/agentLookup';
import { IAgent } from 'slices/agentManagement/types';
import IssuancePlanSelect from '../shared/IssuancePlanSelect';
import AgentSelect from '../shared/AgentSelect';
import { cleanParams, formatDateTime, formatQty, formatCurrency, getPaginatedPayload } from '../shared/formatters';
import { downloadReportExport } from '../shared/exportReport';
import { INITIAL_REPORT_PAGINATION, ReportPaginationState, TicketDeliveryLineReportRow } from '../shared/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('reports_issuance_reporting');

const DeliveryLinesTab: React.FC = () => {
  const [planId, setPlanId] = useState('');
  const [agentId, setAgentId] = useState('');
  const [symbol, setSymbol] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<'Xlsx' | 'Csv' | null>(null);
  const [rows, setRows] = useState<TicketDeliveryLineReportRow[]>([]);
  const [pagination, setPagination] = useState<ReportPaginationState>(INITIAL_REPORT_PAGINATION);
  const [agents, setAgents] = useState<IAgent[]>([]);
  const agentMap = useMemo(() => buildAgentMap(agents), [agents]);

  useEffect(() => { loadAgentsWithCache().then(setAgents); }, []);

  const buildFilterParams = useCallback(
    () => ({
      IssuancePlanId: planId || undefined,
      AgentId: agentId || undefined,
      Symbol: symbol || undefined,
      FromDate: fromDate || undefined,
      ToDate: toDate || undefined,
    }),
    [planId, agentId, symbol, fromDate, toDate],
  );

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTicketDeliveryLineReportApi(
        cleanParams({ ...buildFilterParams(), PageIndex: pagination.currentPage, PageSize: pagination.pageSize }),
      );
      const { items, totalItems, totalPages } = getPaginatedPayload(res);
      setRows(items);
      setPagination((prev) => ({ ...prev, totalItems, totalPages }));
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_tai_bao_cao_chi_message'));
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams, pagination.currentPage, pagination.pageSize]);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize, planId, agentId, symbol, fromDate, toDate]);

  const resetPage = () => setPagination((prev) => ({ ...prev, currentPage: 1 }));

  const handleExport = async (format: 'Xlsx' | 'Csv') => {
    setExporting(format);
    try {
      await downloadReportExport(`${ISSUANCE_REPORTS_BASE}/DeliveryLines`, buildFilterParams(), format, 'ChiTietGiaoVeDaiLy');
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_xuat_bao_cao_message'));
    } finally {
      setExporting(null);
    }
  };

  return (
    <>
      <Row className="g-3 mb-3 align-items-end">
        <Col md={3}>
          <Label className="form-label">{st('ky_phat_hanh_label')}</Label>
          <IssuancePlanSelect value={planId} onChange={(v) => { setPlanId(v); resetPage(); }} />
        </Col>
        <Col md={3}>
          <Label className="form-label">{st('dai_ly_label')}</Label>
          <AgentSelect value={agentId} onChange={(v) => { setAgentId(v); resetPage(); }} />
        </Col>
        <Col md={2}>
          <Label className="form-label">{st('ky_hieu_label')}</Label>
          <Input type="text" placeholder={st('vd_n_b_placeholder')} value={symbol} onChange={(e) => { setSymbol(e.target.value); resetPage(); }} />
        </Col>
        <Col md={2}>
          <Label className="form-label">{st('tu_ngay_label')}</Label>
          <Input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); resetPage(); }} />
        </Col>
        <Col md={2}>
          <Label className="form-label">{st('den_ngay_label')}</Label>
          <Input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); resetPage(); }} />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col className="d-flex gap-2 justify-content-end">
          <Button color="success" outline size="sm" disabled={!!exporting} onClick={() => handleExport('Xlsx')}>
            {exporting === 'Xlsx' ? <Spinner size="sm" /> : <i className="ri-file-excel-2-line align-bottom me-1"></i>}
            {st('excel_button')}
          </Button>
          <Button color="secondary" outline size="sm" disabled={!!exporting} onClick={() => handleExport('Csv')}>
            {exporting === 'Csv' ? <Spinner size="sm" /> : <i className="ri-file-text-line align-bottom me-1"></i>}
            {st('csv_button')}
          </Button>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5"><Spinner color="primary" /></div>
      ) : (
        <>
          <DataTableShell
            headerRow={
              <tr>
                <th className="text-center" style={{ width: 50 }}>{st('stt_column')}</th>
                <th>{st('ma_phieu_column')}</th>
                <th>{st('dai_ly_label')}</th>
                <th>{st('ky_hieu_label')}</th>
                <th>{st('tu_so_column')}</th>
                <th>{st('den_so_column')}</th>
                <th className="text-end">{st('sl_column')}</th>
                <th className="text-end">{st('gia_tri_column')}</th>
                <th>{st('ngay_giao_column')}</th>
                <th>{st('trang_thai_column')}</th>
              </tr>
            }
          >
            {rows.length === 0 ? (
              <tr><td colSpan={10} className="text-center text-muted py-5">{st('khong_co_du_lieu_text')}</td></tr>
            ) : (
              rows.map((row, index) => (
                <tr key={`${row.deliveryId}-${row.symbol}-${row.rangeStart}`}>
                  <td className="text-center">{getSttNumber(pagination.currentPage, pagination.pageSize, index)}</td>
                  <td>{row.deliveryCode}</td>
                  <td>
                    <div className="fw-medium">{getAgentName(agentMap, row.agentId)}</div>
                    <small className="text-muted">{getAgentCode(agentMap, row.agentId)}</small>
                  </td>
                  <td>{row.symbol}</td>
                  <td>{row.rangeStart}</td>
                  <td>{row.rangeEnd}</td>
                  <td className="text-end">{formatQty(row.quantity)}</td>
                  <td className="text-end">{formatCurrency(row.value)}</td>
                  <td>{formatDateTime(row.deliveredAt)}</td>
                  <td><span className="badge bg-info-subtle text-info">{row.statusName}</span></td>
                </tr>
              ))
            )}
          </DataTableShell>

          <AgentPagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            pageSize={pagination.pageSize}
            onPageChange={(page) => setPagination((prev) => ({ ...prev, currentPage: page }))}
          />
        </>
      )}
    </>
  );
};

export default DeliveryLinesTab;
